import React, { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "../lib/apiClient";
import { INDIAN_STATES } from "../lib/constants";
import PageHeader from "../components/PageHeader";
import { toast } from "sonner";
import { FieldStyle } from "./PartiesPage";
import { useAuth } from "../context/AuthContext";
import { UploadSimple, Image as ImageIcon } from "@phosphor-icons/react";

export default function CompanyPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploadingField, setUploadingField] = useState("");
  const logoRef = useRef(null);
  const sigRef = useRef(null);

  useEffect(() => {
    api.get("/company").then(r => setForm(r.data || {})).catch((err) => { console.error('Company load failed:', err); setForm({}); });
  }, []);

  if (!form) return <div className="p-8 text-ink-muted">Loading…</div>;

  const save = async () => {
    setSaving(true);
    try { await api.put("/company", form); toast.success("Company profile updated"); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || e.message); }
    finally { setSaving(false); }
  };

  const uploadImage = async (file, field) => {
    if (!file) return;
    setUploadingField(field);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const next = { ...form, [field]: data.url };
      setForm(next);
      // also persist immediately so sidebar/invoices reflect it
      await api.put("/company", next);
      toast.success(`${field === "logo_url" ? "Logo" : "Signature"} uploaded`);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    } finally { setUploadingField(""); }
  };

  const onStateChange = (code) => {
    const st = INDIAN_STATES.find(s => s.code === code);
    setForm(f => ({ ...f, state_code: code, state: st?.name || "" }));
  };

  const Input = ({ k, label, full, mono }) => (
    <div className={full ? "col-span-2" : ""}>
      <div className="label-overline block mb-1.5">{label}</div>
      <input
        data-testid={`company-${k}`}
        className={`field ${mono ? "font-mono" : ""}`}
        disabled={!isAdmin}
        value={form[k] || ""}
        onChange={e => setForm({ ...form, [k]: e.target.value })}
      />
    </div>
  );

  return (
    <div data-testid="company-page">
      <PageHeader
        title="Company Profile"
        subtitle="Used on every invoice, challan, and e-way bill JSON."
        actions={isAdmin && <button onClick={save} disabled={saving} className="btn-primary" data-testid="save-company">{saving ? "Saving…" : "Save Changes"}</button>}
      />

      <div className="p-8 max-w-4xl space-y-6">
        <div className="card-base p-6 grid grid-cols-2 gap-5">
          <Input k="legal_name" label="Legal Name" full />
          <Input k="trade_name" label="Trade Name" full />
          <Input k="gstin" label="GSTIN" mono />
          <Input k="pan" label="PAN" mono />
          <div className="col-span-2">
            <div className="label-overline block mb-1.5">Address Line</div>
            <textarea rows={2} className="field" disabled={!isAdmin} value={form.address_line || ""} onChange={e => setForm({ ...form, address_line: e.target.value })} data-testid="company-address_line" />
          </div>
          <Input k="city" label="City" />
          <div>
            <div className="label-overline block mb-1.5">State</div>
            <select className="field" disabled={!isAdmin} value={form.state_code || ""} onChange={e => onStateChange(e.target.value)} data-testid="company-state">
              <option value="">Select state</option>
              {INDIAN_STATES.map(s => <option key={s.code} value={s.code}>{s.code} - {s.name}</option>)}
            </select>
          </div>
          <Input k="pincode" label="Pincode" />
          <Input k="phone" label="Phone" />
          <Input k="email" label="Email" />
        </div>

        <div>
          <div className="label-overline mb-3">Brand Assets</div>
          <div className="card-base p-6 grid grid-cols-1 md:grid-cols-2 gap-5">
            <ImageUploader
              label="Company Logo"
              src={form.logo_url}
              disabled={!isAdmin}
              uploading={uploadingField === "logo_url"}
              inputRef={logoRef}
              onPick={(f) => uploadImage(f, "logo_url")}
              onClear={async () => { const next = { ...form, logo_url: "" }; setForm(next); await api.put("/company", next); toast.success("Logo removed"); }}
              testId="company-logo-upload"
            />
            <ImageUploader
              label="Authorised Signature"
              src={form.signature_url}
              disabled={!isAdmin}
              uploading={uploadingField === "signature_url"}
              inputRef={sigRef}
              onPick={(f) => uploadImage(f, "signature_url")}
              onClear={async () => { const next = { ...form, signature_url: "" }; setForm(next); await api.put("/company", next); toast.success("Signature removed"); }}
              testId="company-signature-upload"
            />
          </div>
        </div>

        <div>
          <div className="label-overline mb-3">Bank Details (shown on invoices)</div>
          <div className="card-base p-6 grid grid-cols-2 gap-5">
            <Input k="bank_name" label="Bank Name" />
            <Input k="bank_branch" label="Branch" />
            <Input k="bank_account" label="Account Number" mono />
            <Input k="bank_ifsc" label="IFSC Code" mono />
          </div>
        </div>

        {!isAdmin && <div className="text-xs text-ink-muted">Only admins can edit the company profile.</div>}
      </div>

      <FieldStyle />
    </div>
  );
}

function ImageUploader({ label, src, disabled, uploading, inputRef, onPick, onClear, testId }) {
  return (
    <div data-testid={testId}>
      <div className="label-overline block mb-2">{label}</div>
      <div className="flex items-center gap-4">
        <div className="w-24 h-24 rounded-md border border-border bg-surface-muted flex items-center justify-center overflow-hidden">
          {src ? <img src={src} alt={label} className="w-full h-full object-contain bg-white" /> : <ImageIcon size={28} weight="duotone" className="text-ink-muted" />}
        </div>
        <div className="flex-1 space-y-2">
          <input ref={inputRef} type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" className="hidden" onChange={e => { if (e.target.files?.[0]) onPick(e.target.files[0]); e.target.value = ""; }} />
          <button type="button" disabled={disabled || uploading} onClick={() => inputRef.current?.click()} className="btn-secondary inline-flex items-center gap-1.5 text-sm">
            <UploadSimple size={14} weight="bold" /> {uploading ? "Uploading…" : src ? "Replace" : "Upload"}
          </button>
          {src && !disabled && <button type="button" onClick={onClear} className="btn-ghost text-destructive text-xs ml-2">Remove</button>}
          <div className="text-[11px] text-ink-muted">PNG, JPG, WEBP or SVG. Max 2 MB.</div>
        </div>
      </div>
    </div>
  );
}
